import { useCallback, useState } from 'react';

/**
 * `useCursorPages` — the exact back-stack every list screen navigates with.
 *
 * ## Why a stack, not a page number
 *
 * Every list endpoint is cursor-paginated (`cursor` + `per_page` in, `meta.
 * next_cursor` + `meta.total` out). There is no page number to jump to and
 * there never was — the API answers "here is the next cursor", never "here is
 * page 7". The API also answers no `prev_cursor`, so "Previous" cannot be a
 * request at all: it has to be a client-side memory of where we already were.
 *
 * That memory works because the cursor sequence is DETERMINISTIC for a fixed
 * `(sort, filter, per_page)` — the same request against the same view always
 * mints the same cursor for the same page. Keeping every cursor we have
 * already been handed, in order, and popping the stack on "Previous" is
 * therefore *exact*: page 3's cursor on the way back is the identical string
 * the server gave us on the way there, never a recomputed guess.
 *
 * ## Why `setPerPage` (and `first`) throw the stack away
 *
 * A cursor minted at `per_page=10` encodes a position keyed to that page
 * size. Reusing it at `per_page=50` does not error — the server just returns
 * the wrong slice, silently, which is a worse defect than a page reset ever
 * is. So `setPerPage` — and any change to sort or filter, which the caller
 * enacts by discarding this hook's instance or calling `first()` — resets to
 * `{ stack: [null], index: 0 }` unconditionally. There is no partial-reuse
 * path, on purpose: the moment `per_page` changes, every cursor after page 0
 * is meaningless.
 *
 * ## `observe` records into the stack, once per position
 *
 * `stack[i]` is the cursor a caller passes to *reach* page `i`; `stack[0]` is
 * always `null` (the first page needs no cursor at all). `stack[i + 1]` — the
 * cursor for the page AFTER `i` — is not known until page `i`'s own response
 * arrives and reports `meta.next_cursor`. `observe` is what records that
 * arrival: it appends `meta.next_cursor` to the stack, but ONLY when the
 * stack's tail is still exactly the current index — i.e., only the first time
 * this position's successor is learned.
 *
 * That guard is the whole idempotence contract. A duplicate `observe` call
 * for a position already recorded — a second render of the same response, a
 * retried request whose result already landed — is a no-op: the stack does
 * not grow, and a later `next()`/`prev()` walk is unaffected. Without the
 * guard, a duplicate call would append a second entry, shifting every later
 * index by one and handing `next()` the wrong slot.
 *
 * `next()` never talks to the network and never invents a cursor: it only
 * advances into a slot `observe` has already filled. `canNext` is therefore
 * "do we already know what's after this page", not "did the last response
 * say there was one" — the two happen to be the same fact, read from the same
 * place.
 */

export type PageMeta = { next_cursor: string | null; total: number | null };

export type CursorPages = {
  /** Pass to the API verbatim; `null` means "first page, no cursor". */
  cursor: string | null;
  perPage: number;
  canPrev: boolean;
  canNext: boolean;
  /** 0-based, for "Showing X–Y". */
  pageIndex: number;
  next(): void;
  prev(): void;
  first(): void;
  setPerPage(n: number): void;
  /** Call with each response's meta. Idempotent per cursor. */
  observe(meta: PageMeta): void;
};

interface CursorState {
  /** `stack[i]` is the cursor that reaches page `i`. `stack[0]` is always `null`. */
  stack: (string | null)[];
  index: number;
  perPage: number;
}

const DEFAULT_PER_PAGE = 10;

function initialState(perPage: number): CursorState {
  return { stack: [null], index: 0, perPage };
}

export function useCursorPages(initialPerPage: number = DEFAULT_PER_PAGE): CursorPages {
  const [state, setState] = useState<CursorState>(() => initialState(initialPerPage));

  const next = useCallback(() => {
    setState((s) => (s.stack.length > s.index + 1 ? { ...s, index: s.index + 1 } : s));
  }, []);

  const prev = useCallback(() => {
    setState((s) => (s.index > 0 ? { ...s, index: s.index - 1 } : s));
  }, []);

  const first = useCallback(() => {
    setState((s) => (s.index === 0 && s.stack.length === 1 ? s : { stack: [null], index: 0, perPage: s.perPage }));
  }, []);

  const setPerPage = useCallback((n: number) => {
    setState((s) => (s.perPage === n && s.index === 0 && s.stack.length === 1 ? s : initialState(n)));
  }, []);

  const observe = useCallback((meta: PageMeta) => {
    setState((s) => {
      // Idempotence guard: only the page CURRENTLY at the stack's tail can
      // learn a new successor. A duplicate call for a position already
      // recorded — the defect this guards against — must not push again.
      if (s.stack.length !== s.index + 1) return s;
      if (meta.next_cursor === null) return s;
      return { ...s, stack: [...s.stack, meta.next_cursor] };
    });
  }, []);

  return {
    cursor: state.stack[state.index] ?? null,
    perPage: state.perPage,
    canPrev: state.index > 0,
    canNext: state.stack.length > state.index + 1,
    pageIndex: state.index,
    next,
    prev,
    first,
    setPerPage,
    observe,
  };
}
